import { useEffect, useMemo, useRef, useState } from "react";
import type { GasStoragePayload, GasStorageBandPoint, GasStorageYearPoint } from "../lib/api";
import { ChartCard } from "./ChartCard";

interface Props {
  data: GasStoragePayload;
}

const COLORS = {
  band: "rgba(148, 163, 184, 0.25)",
  bandEdge: "rgba(148, 163, 184, 0.45)",
  now: "#fbbf24", // amber-400, matches the gas accent
  prev: "rgba(167, 139, 250, 0.7)", // violet, dashed
  nowMarker: "rgba(148, 163, 184, 0.55)",
  grid: "rgba(148, 163, 184, 0.18)",
};

const PADDING = { top: 14, right: 16, bottom: 28, left: 36 };

/**
 * Seasonal storage card: 5-year min/max envelope, previous year as a dashed
 * reference line, current year filled in solid amber up to today, plus a
 * crosshair tooltip that reports min/max/prev/now at the hovered day-of-year.
 *
 * The x-axis covers the entire calendar (DOY 1–366); months tick at the
 * first day of each month. The y-axis is fixed at 0–100 % so seasonal shape
 * stays comparable year-over-year.
 */
export function GasStorageChart({ data }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  const height = 320;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setWidth(Math.max(320, Math.floor(entry.contentRect.width)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const innerW = Math.max(0, width - PADDING.left - PADDING.right);
  const innerH = Math.max(0, height - PADDING.top - PADDING.bottom);

  // x maps DOY 1–366 to the inner chart area; y maps 0–100 % top-down.
  const xScale = (doy: number) => PADDING.left + ((doy - 1) / 365) * innerW;
  const yScale = (pct: number) => PADDING.top + (1 - pct / 100) * innerH;

  // Build SVG path strings up-front so the render stays simple. null gaps in
  // any series open a new sub-path (M …) rather than connecting through 0.
  const bandPath = useMemo(() => buildBandPath(data.band, xScale, yScale), [data.band, innerW, innerH]); // eslint-disable-line react-hooks/exhaustive-deps
  const currentPath = useMemo(
    () => buildLinePath(data.currentYear, xScale, yScale),
    [data.currentYear, innerW, innerH], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const prevPath = useMemo(
    () => buildLinePath(data.prevYear, xScale, yScale),
    [data.prevYear, innerW, innerH], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // "Heute"-Marker am letzten current-year-Punkt.
  const lastCurrent = data.currentYear.length > 0 ? data.currentYear[data.currentYear.length - 1] : null;
  const nowX = lastCurrent ? xScale(lastCurrent.doy) : null;
  const nowY = lastCurrent && lastCurrent.pct !== null ? yScale(lastCurrent.pct) : null;

  // Tooltip state — hover x in DOY (1..366).
  const [hoverDoy, setHoverDoy] = useState<number | null>(null);
  const onPointer = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    if (localX < PADDING.left || localX > PADDING.left + innerW) {
      setHoverDoy(null);
      return;
    }
    const frac = (localX - PADDING.left) / innerW;
    setHoverDoy(Math.round(frac * 365) + 1);
  };

  const hovered = hoverDoy !== null ? lookupAtDoy(data, hoverDoy) : null;
  const tooltipX = hoverDoy !== null ? xScale(hoverDoy) : null;

  const delta =
    data.current.pct !== null && data.yearAgo.pct !== null
      ? round1(data.current.pct - data.yearAgo.pct)
      : null;

  return (
    <ChartCard
      title="Gasspeicherstände Deutschland"
      unit="%"
      accent="amber"
      updatedAt={data.updatedAt}
      footer={
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-4 h-2.5 rounded-sm" style={{ background: COLORS.band }} />
            Min/Max 5 Jahre ({data.bandYears.length > 0
              ? `${data.bandYears[data.bandYears.length - 1]}–${data.bandYears[0]}`
              : "—"})
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-4 border-t-2"
              style={{ borderColor: COLORS.prev, borderStyle: "dashed" }}
            />
            Vorjahr ({data.currentYearLabel - 1})
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-4 border-t-2" style={{ borderColor: COLORS.now }} />
            Aktuelles Jahr ({data.currentYearLabel})
          </span>
          <span className="ml-auto text-[11px] opacity-80">Quelle: AGSI · DF_80 / DF_72</span>
        </div>
      }
    >
      {hasError(data) && (
        <div className="mb-2 text-[11px] text-rose-700 dark:text-rose-300">
          Teilweise unvollständige Daten:{" "}
          {Object.entries(data.errors)
            .map(([k, v]) => `${k}: ${v}`)
            .join(" · ")}
        </div>
      )}

      <div className="grid grid-cols-[auto_auto_1fr] items-end gap-x-7 gap-y-1 mb-3">
        <Summary
          label="Aktuell"
          value={data.current.pct}
          sub={fmtSubDate(data.current.date)}
          colorClass="text-amber-500 dark:text-amber-300"
          big
          twh={data.current.inv}
          cap={data.current.cap}
        />
        <Summary
          label={`Vorjahr (selber Tag)`}
          value={data.yearAgo.pct}
          sub={fmtSubDate(data.yearAgo.date)}
          colorClass="text-violet-500 dark:text-violet-300"
          twh={data.yearAgo.inv}
          cap={data.yearAgo.cap}
        />
        <div className="text-right text-[11px] text-slate-500 dark:text-slate-400">
          <div>Δ Vorjahr</div>
          {delta === null ? (
            <span className="text-slate-400">—</span>
          ) : (
            <span
              className={
                "inline-block mt-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold tabular-nums " +
                // Buyer perspective: lower fill → tighter supply → bad (red);
                // higher fill → comfortable supply → good (green).
                (delta > 0
                  ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                  : delta < 0
                    ? "bg-rose-500/20 text-rose-700 dark:text-rose-300"
                    : "bg-slate-500/15 text-slate-600 dark:text-slate-300")
              }
            >
              {(delta > 0 ? "+" : "") + delta.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %-Pkt
            </span>
          )}
        </div>
      </div>

      <div ref={containerRef} className="relative">
        <svg
          width={width}
          height={height}
          role="img"
          aria-label="Saisonchart Gasspeicherstände"
          onPointerMove={onPointer}
          onPointerLeave={() => setHoverDoy(null)}
          className="touch-none"
        >
          {/* y-axis gridlines + labels */}
          {[0, 25, 50, 75, 100].map((pct) => {
            const y = yScale(pct);
            return (
              <g key={pct}>
                <line
                  x1={PADDING.left}
                  x2={PADDING.left + innerW}
                  y1={y}
                  y2={y}
                  stroke={COLORS.grid}
                  strokeWidth={1}
                />
                <text
                  x={PADDING.left - 6}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  style={{ fontSize: 10 }}
                  className="fill-slate-500 dark:fill-slate-400"
                >
                  {pct} %
                </text>
              </g>
            );
          })}

          {/* x-axis month ticks */}
          {MONTH_FIRST_DOY.map(([label, doy]) => (
            <g key={label}>
              <line
                x1={xScale(doy)}
                x2={xScale(doy)}
                y1={PADDING.top + innerH}
                y2={PADDING.top + innerH + 4}
                stroke={COLORS.grid}
                strokeWidth={1}
              />
              <text
                x={xScale(doy)}
                y={PADDING.top + innerH + 16}
                textAnchor="middle"
                style={{ fontSize: 10 }}
                className="fill-slate-500 dark:fill-slate-400"
              >
                {label}
              </text>
            </g>
          ))}

          {/* Min/max band (filled polygon) */}
          {bandPath.area && (
            <path d={bandPath.area} fill={COLORS.band} stroke={COLORS.bandEdge} strokeWidth={1} />
          )}

          {/* Previous year — dashed line */}
          {prevPath && (
            <path
              d={prevPath}
              fill="none"
              stroke={COLORS.prev}
              strokeWidth={1.6}
              strokeDasharray="5 4"
            />
          )}

          {/* Current year — solid */}
          {currentPath && (
            <path d={currentPath} fill="none" stroke={COLORS.now} strokeWidth={2.2} strokeLinecap="round" />
          )}

          {/* "Heute" Vertikallinie + Punkt */}
          {nowX !== null && (
            <line
              x1={nowX}
              x2={nowX}
              y1={PADDING.top}
              y2={PADDING.top + innerH}
              stroke={COLORS.nowMarker}
              strokeWidth={1}
              strokeDasharray="2 3"
            />
          )}
          {nowX !== null && nowY !== null && <circle cx={nowX} cy={nowY} r={3.5} fill={COLORS.now} />}

          {/* Hover crosshair */}
          {tooltipX !== null && (
            <line
              x1={tooltipX}
              x2={tooltipX}
              y1={PADDING.top}
              y2={PADDING.top + innerH}
              stroke="rgba(148, 163, 184, 0.5)"
              strokeWidth={1}
            />
          )}
        </svg>

        {/* Tooltip */}
        {tooltipX !== null && hovered && (
          <div
            className="pointer-events-none absolute z-10 bg-slate-900/95 dark:bg-slate-800/95 text-white text-xs rounded-md shadow-lg px-2.5 py-1.5 ring-1 ring-slate-700"
            style={{
              left:
                tooltipX > width - 170
                  ? Math.max(8, tooltipX - 180)
                  : Math.min(width - 170, tooltipX + 12),
              top: 10,
              minWidth: 160,
            }}
          >
            <div className="text-[10px] uppercase tracking-wide text-slate-300 mb-1">
              {fmtMonthDay(hovered.monthDay)}
            </div>
            <ul className="space-y-0.5">
              <Row label="Aktuell" value={hovered.current} unit="%" color={COLORS.now} />
              <Row label="Vorjahr" value={hovered.prev} unit="%" color={COLORS.prev} dashed />
              <Row label="Min 5J" value={hovered.min} unit="%" color={COLORS.bandEdge} muted />
              <Row label="Max 5J" value={hovered.max} unit="%" color={COLORS.bandEdge} muted />
            </ul>
          </div>
        )}
      </div>
    </ChartCard>
  );
}

// ---- Summary headline pill -----------------------------------------------

function Summary({
  label,
  value,
  sub,
  colorClass,
  big = false,
  twh,
  cap,
}: {
  label: string;
  value: number | null;
  sub: string | null;
  colorClass: string;
  big?: boolean;
  twh: number | null;
  cap: number | null;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div
        className={`tabular-nums font-semibold ${colorClass} ${big ? "text-2xl sm:text-3xl" : "text-lg sm:text-xl"}`}
      >
        {fmtPct(value)}
      </div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400">
        {sub}
        {twh !== null && cap !== null && (
          <span className="ml-1 opacity-80">· {fmtTwh(twh)} / {fmtTwh(cap)} TWh</span>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  unit,
  color,
  dashed = false,
  muted = false,
}: {
  label: string;
  value: number | null;
  unit: string;
  color: string;
  dashed?: boolean;
  muted?: boolean;
}) {
  return (
    <li className={`flex items-center gap-2 leading-tight ${muted ? "opacity-70" : ""}`}>
      <span
        className="inline-block w-3 h-0 border-t-2"
        style={{ borderColor: color, borderStyle: dashed ? "dashed" : "solid" }}
      />
      <span className="grow">{label}</span>
      <span className="tabular-nums">
        {value === null ? "—" : `${value.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${unit}`}
      </span>
    </li>
  );
}

// ---- Path builders --------------------------------------------------------

function buildBandPath(
  band: readonly GasStorageBandPoint[],
  xScale: (doy: number) => number,
  yScale: (pct: number) => number,
): { area: string | null } {
  if (band.length === 0) return { area: null };
  // Walk top edge (max) left-to-right, then bottom edge (min) right-to-left,
  // closing with Z to make a single filled polygon. Skips any DOY where
  // min or max is null so leap-day gaps don't drag the band to zero.
  const top: string[] = [];
  const bottom: string[] = [];
  for (const p of band) {
    if (p.max === null) continue;
    top.push(`${xScale(p.doy).toFixed(1)} ${yScale(p.max).toFixed(1)}`);
  }
  for (let i = band.length - 1; i >= 0; i--) {
    const p = band[i];
    if (p.min === null) continue;
    bottom.push(`${xScale(p.doy).toFixed(1)} ${yScale(p.min).toFixed(1)}`);
  }
  if (top.length === 0 || bottom.length === 0) return { area: null };
  return {
    area: `M ${top[0]} L ${top.slice(1).join(" L ")} L ${bottom.join(" L ")} Z`,
  };
}

function buildLinePath(
  series: readonly GasStorageYearPoint[],
  xScale: (doy: number) => number,
  yScale: (pct: number) => number,
): string | null {
  if (series.length === 0) return null;
  let d = "";
  let penDown = false;
  for (const p of series) {
    if (p.pct === null) {
      penDown = false;
      continue;
    }
    const x = xScale(p.doy).toFixed(1);
    const y = yScale(p.pct).toFixed(1);
    d += penDown ? ` L ${x} ${y}` : `M ${x} ${y}`;
    penDown = true;
  }
  return d || null;
}

// ---- Lookup for tooltip ---------------------------------------------------

interface HoveredValues {
  monthDay: string;
  current: number | null;
  prev: number | null;
  min: number | null;
  max: number | null;
}

function lookupAtDoy(data: GasStoragePayload, doy: number): HoveredValues {
  const band = nearestByDoy(data.band, doy);
  const cur = nearestByDoy(data.currentYear, doy);
  const prev = nearestByDoy(data.prevYear, doy);
  const monthDay = cur?.monthDay ?? prev?.monthDay ?? band?.monthDay ?? doyToMonthDay(doy);
  return {
    monthDay,
    current: cur?.pct ?? null,
    prev: prev?.pct ?? null,
    min: band?.min ?? null,
    max: band?.max ?? null,
  };
}

function nearestByDoy<T extends { doy: number }>(arr: readonly T[], doy: number): T | null {
  let best: T | null = null;
  let bestDt = Infinity;
  for (const p of arr) {
    const dt = Math.abs(p.doy - doy);
    if (dt < bestDt) {
      bestDt = dt;
      best = p;
    }
  }
  return bestDt <= 5 ? best : null;
}

function doyToMonthDay(doy: number): string {
  // Use 2024 (a leap year) so DOY 366 maps cleanly.
  const d = new Date(Date.UTC(2024, 0, 1));
  d.setUTCDate(doy);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${mm}-${dd}`;
}

// ---- Formatters / constants ----------------------------------------------

const MONTH_FIRST_DOY: ReadonlyArray<[string, number]> = [
  ["Jan", 1], ["Feb", 32], ["Mrz", 60], ["Apr", 91],
  ["Mai", 121], ["Jun", 152], ["Jul", 182], ["Aug", 213],
  ["Sep", 244], ["Okt", 274], ["Nov", 305], ["Dez", 335],
];

function fmtPct(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return `${n.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

function fmtTwh(n: number): string {
  return n.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function fmtSubDate(ymd: string | null): string | null {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-");
  return `${d}.${m}.${y}`;
}

function fmtMonthDay(mmDd: string): string {
  const [m, d] = mmDd.split("-");
  return `${d}.${m}.`;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function hasError(data: GasStoragePayload): boolean {
  return Object.keys(data.errors).length > 0;
}
